<template>
  <layout-waiter :nav-menu-items="navMenuItems">
    <slot v-if="!isLoading" />

    <!-- Slot: Navbar -->
    <template #navbar="{ toggleVerticalNavMenuActive }">
      <div class="navbar-content-container">
        <!-- Left Content: -->
        <div class="d-flex align-center">
          <v-icon
            v-if="$vuetify.breakpoint.mdAndDown"
            class="me-3"
            @click="toggleVerticalNavMenuActive"
          >
            {{ icons.mdiMenu }}
          </v-icon>

          <v-btn
            v-if="['staging', 'development'].includes(currentEnv)"
            color="error"
            :ripple="false"
            small
          >
            {{
              {
                staging: 'STG',
                development: 'DEV'
              }[currentEnv]
            }}
          </v-btn>
        </div>

        <!-- Right Content: -->
        <div class="d-flex align-center right-row">
          <clock class="mr-4" />
          <v-btn
            icon
            depressed
            :ripple="false"
            class="mr-1"
            @click="showCheckinDialog = true"
          >
            <v-icon>
              {{ icons.mdiQrcodeScan }}
            </v-icon>
          </v-btn>
          <v-btn
            icon
            depressed
            :ripple="false"
            :loading="isLoading"
            @click="$router.go(0)"
          >
            <v-icon>
              {{ icons.mdiReload }}
            </v-icon>
          </v-btn>
          <app-bar-theme-switcher class="mr-2" />
          <template v-if="hasMonitorPermission">
            <app-bar-notification
              :notifications="currentClubNotifications"
              :color="bellColor"
              class="mr-2"
              @update:close-notification-card="setColorEmpty"
            />
            <monitor-menu class="mr-5" />
            <cash-register-menu />
          </template>
          <v-btn
            v-else
            icon
            depressed
            :ripple="false"
            @click="logoutUser"
          >
            <v-icon>
              {{ icons.mdiLogoutVariant }}
            </v-icon>
          </v-btn>
        </div>
      </div>
    </template>

    <!-- Slot: Footer -->
    <template #footer>
      <!-- TODO -->
    </template>

    <checkin-dialog v-model="showCheckinDialog" />
  </layout-waiter>
</template>

<script>
import AppBarThemeSwitcher from '@core/layouts/components/app-bar/AppBarThemeSwitcher.vue'
import AppBarNotification from '@core/layouts/components/app-bar/WaiterAppBarNotification.vue'
import MonitorMenu from '@core/layouts/components/app-bar/MonitorMenu.vue'
import CashRegisterMenu from '@core/layouts/components/app-bar/CashRegisterMenu.vue'

import {
  mdiMenu, mdiReload, mdiLogoutVariant, mdiQrcodeScan,
} from '@mdi/js'
import {
  reactive,
  provide,
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  getCurrentInstance,
} from '@vue/composition-api'
import CheckinDialog from '@/views/components/checkin/CheckinDialog.vue'

import navMenuItems from '@/navigation/custom'
import LayoutWaiter from '@/@core/layouts/variants/LayoutWaiter.vue'

import Clock from '@/views/components/util/Clock.vue'
import { cable } from '@/plugins/websocket-client'
import { signOut, clearAuthLocalStorage } from '@/api/auth'
import { initialAbility } from '@/plugins/acl/config'
import router from '@/router'

import ClubApi from '@/api/waiter/Club'
import ReportApi from '@/api/waiter/Report'
import ReceiptNotePresetApi from '@/api/v2/utility/settings/ReceiptNotePreset'
import CustomerTagApi from '@/api/waiter/CustomerTag'
import MonitorTimecardApi from '@/api/v2/monitor/Timecard'
import MonitorTableApi from '@/api/v2/monitor/Table'
import MonitorVendingHistoryApi from '@/api/v2/monitor/VendingHistory'
import MonitorKpiApi from '@/api/v2/monitor/Kpi'
import useReceiptPrinterConnection from '@/views/composable/useReceiptPrinterConnection'
import useCompInit from '@/views/composable/useCompInit'
import useDateFormat from '@/views/composable/useDateFormat'
import useAudioPlayer from '@/views/composable/useAudioPlayer'
import useKitchenPrintWatcher from '@/views/composable/useKitchenPrintWatcher'

export default {
  components: {
    LayoutWaiter,
    AppBarThemeSwitcher,
    AppBarNotification,
    Clock,
    CashRegisterMenu,
    MonitorMenu,
    CheckinDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    const { isLoading, initWith } = useCompInit()
    const { dateTime } = useDateFormat()
    const {
      isConnected,
      isConnecting,
      printer,
      checkConnection,
    } = useReceiptPrinterConnection()

    // AudioPlayer: iOS/iPadOS向け通知音アンロック機構
    const {
      setupUnlockListeners,
      preloadSounds,
      playSound,
    } = useAudioPlayer()

    const hasMonitorPermission = computed(() => vm.$can('read', 'custom:monitor'))
    const showCheckinDialog = ref(false)

    const currentClubMeta = reactive({
      remainingCashRegisterMoney: 0,
      todaySalesUnfixed: 0,
      todaySales: 0,
      today: new Date().toISOString().substr(0, 10),
    })
    const receiptNotePresets = ref([])
    const customerTags = ref([])

    // モニター権限用データ
    const currentClubNotifications = ref([])
    const bellColor = ref('')
    const currentStats = reactive({
      castCount: 0,
      tableCount: 0,
      customerCount: 0,
      castWorkingTime: 0,
      customerUsageTime: 0,
    })
    const kpiStats = reactive({
      customerCount: 0,
      salesAmount: 0,
      customKpis: [],
    })

    const getClub = async () => {
      const res = await ClubApi.getClub()

      if (res?.data?.data) {
        const {
          remainingCashRegisterMoney,
          todaySalesUnfixed,
          todaySales,
          today,
        } = res.data.data.meta

        currentClubMeta.remainingCashRegisterMoney = remainingCashRegisterMoney
        currentClubMeta.todaySalesUnfixed = todaySalesUnfixed
        currentClubMeta.todaySales = todaySales
        currentClubMeta.today = today
      }
    }

    const getReceiptNotePresets = async () => {
      try {
        const res = await ReceiptNotePresetApi.getReceiptNotePresets()
        receiptNotePresets.value = res.data.data.data
      } catch {
        // customロールではアクセスできない場合があるためエラーを無視
      }
    }

    const getCustomerTags = async () => {
      const res = await CustomerTagApi.getCustomerTags()
      if (res?.data) {
        customerTags.value = [...res.data.customerTags.data]
      }
    }

    // モニター権限用API呼び出し
    const getReport = async () => {
      const res = await ReportApi.getReports()
      if (res?.data?.data) {
        currentClubNotifications.value = res.data.data.map(report => ({
          ...report.attributes.report,
          time: dateTime(report.attributes.report.time),
        }))
      }
    }

    const getActiveCastCount = async () => {
      try {
        const res = await MonitorTimecardApi.getActiveCastCount()
        currentStats.castCount = res.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }

    const getActiveTableCounts = async () => {
      try {
        const res = await MonitorTableApi.getActiveTableCounts()
        const { tableCount, customerCount } = res.data.data
        currentStats.tableCount = tableCount
        currentStats.customerCount = customerCount
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }

    const fetchPlusMinusStats = async () => {
      await Promise.all([getActiveCastCount(), getActiveTableCounts()])
    }

    const getCastWorkingTime = async () => {
      try {
        const res = await MonitorTimecardApi.getCastWorkingTime()
        currentStats.castWorkingTime = res.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }

    const getCustomerUsageTime = async () => {
      try {
        const res = await MonitorVendingHistoryApi.getCustomerUsageTime()
        currentStats.customerUsageTime = res.data.data
      } catch (error) {
        vm.$toast.error(error.response.data.message.join('\n'))
      }
    }

    const fetchOperationRateStats = async () => {
      if (!hasMonitorPermission.value) return
      await Promise.all([getCastWorkingTime(), getCustomerUsageTime()])
    }

    const fetchKpiStats = async () => {
      try {
        const res = await MonitorKpiApi.getKpis()
        const { customer_count, sales_amount, custom_kpis } = res.data.data
        kpiStats.customerCount = customer_count
        kpiStats.salesAmount = sales_amount
        kpiStats.customKpis = custom_kpis
      } catch (error) {
        vm.$toast.error(error.response?.data?.message?.join('\n') || 'KPIの取得に失敗しました')
      }
    }

    const setColorEmpty = () => {
      bellColor.value = ''
    }

    checkConnection()

    // initWith: モニター権限がある場合は追加APIも呼び出す
    const initPromises = [
      getClub(),
      getReceiptNotePresets(),
      getCustomerTags(),
    ]

    if (vm.$can('read', 'custom:monitor')) {
      initPromises.push(
        getReport(),
        fetchPlusMinusStats(),
        fetchOperationRateStats(),
        fetchKpiStats(),
      )
    }

    initWith(initPromises)

    // ActionCable NotificationChannel購読
    const subscription = cable.subscriptions.create(
      {
        channel: 'NotificationChannel',
      },
      {
        connected: () => {},
        disconnected: () => {},
        received: ({ title, time }) => {
          if (hasMonitorPermission.value) {
            bellColor.value = 'success'
            currentClubNotifications.value.unshift({ title, time: dateTime(time) })
          }
        },
      },
    )

    // キッチンプリンター印刷: モバイルオーダー注文時（重複印刷防止付き）
    useKitchenPrintWatcher()

    // SelfOrderChannel接続 & AudioPlayer初期化
    onMounted(async () => {
      setupUnlockListeners()
      await preloadSounds()
      vm.$store.commit('selfOrder/SET_AUDIO_PLAYER', { playSound })
      vm.$store.dispatch('selfOrder/connect')
    })

    onBeforeUnmount(() => {
      vm.$store.dispatch('selfOrder/disconnect')
      subscription.unsubscribe()
    })

    // LayoutWaiterと同等のprovide（Check.vue等の子コンポーネントが依存）
    provide('currentClubMeta', currentClubMeta)
    provide('getClub', getClub)
    provide('isReceiptPrinterConnected', isConnected)
    provide('isReceiptPrinterConnecting', isConnecting)
    provide('printer', printer)
    provide('noticeSubscription', subscription)
    provide('receiptNotePresets', receiptNotePresets)
    provide('customerTags', customerTags)
    provide('currentStats', currentStats)
    provide('fetchPlusMinusStats', fetchPlusMinusStats)
    provide('fetchOperationRateStats', fetchOperationRateStats)
    provide('kpiStats', kpiStats)
    provide('fetchKpiStats', fetchKpiStats)

    const logoutUser = async () => {
      await signOut()
        .then(() => {
          clearAuthLocalStorage()
          vm.$ability.update(initialAbility)
          router.push({ name: 'auth-login' })
        })
    }

    return {
      navMenuItems,
      isLoading,
      logoutUser,
      hasMonitorPermission,
      currentClubNotifications,
      bellColor,
      setColorEmpty,
      showCheckinDialog,

      icons: {
        mdiMenu,
        mdiReload,
        mdiLogoutVariant,
        mdiQrcodeScan,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.navbar-content-container {
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
  position: relative;
}
</style>
